/**
 * Browser session (02-api-spec §11.2): solves the server's ALTCHA challenge invisibly, opens the session
 * (HttpOnly cookie set by POST /session — never readable here, never in the page HTML), and renews it silently
 * when an endpoint answers 401 SESSION_REQUIRED (expired, other network…), then retries the call once.
 *
 * The solver follows ALTCHA v2, algorithm SHA-256, cost 1 (the only kind of challenge our server issues):
 * find the counter n such that SHA-256(salt ‖ nonce ‖ uint32be(n)) starts with keyPrefix. Counters are tried
 * from 0 in batches of parallel Web Crypto digests, so the page stays responsive.
 */

const hexToBytes = (hex) => Uint8Array.from(hex.match(/../g) ?? [], (b) => parseInt(b, 16));
const bytesToHex = (bytes) => Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');

function startsWith(key, prefix) {
	for (let i = 0; i < prefix.length; i++) {
		if (key[i] !== prefix[i]) {
			return false;
		}
	}
	return true;
}

/**
 * @param {{parameters: object, signature: string}} challenge As returned by POST /session/challenge.
 * @returns {Promise<string|null>} base64 payload {challenge, solution} for POST /session, or null.
 */
export async function solve(challenge, { batch = 500, limit = 5_000_000, subtle = globalThis.crypto?.subtle } = {}) {
	const p = challenge?.parameters;
	if (!subtle || !p || p.algorithm !== 'SHA-256' || p.keyPrefix.length % 2 !== 0) {
		return null;
	}
	const salt = hexToBytes(p.salt);
	const nonce = hexToBytes(p.nonce);
	const prefix = hexToBytes(p.keyPrefix);
	const keyLength = p.keyLength || 32;
	const offset = salt.length + nonce.length;
	const started = Date.now();

	for (let from = 0; from < limit; from += batch) {
		const jobs = [];
		for (let n = from; n < from + batch; n++) {
			const input = new Uint8Array(offset + 4);
			input.set(salt);
			input.set(nonce, salt.length);
			new DataView(input.buffer).setUint32(offset, n, false);
			jobs.push(subtle.digest('SHA-256', input));
		}
		const digests = await Promise.all(jobs);
		for (let i = 0; i < digests.length; i++) {
			let key = new Uint8Array(digests[i]);
			for (let c = 1; c < (p.cost || 1); c++) {
				key = new Uint8Array(await subtle.digest('SHA-256', key));
			}
			key = key.slice(0, keyLength);
			if (startsWith(key, prefix)) {
				const solution = { counter: from + i, derivedKey: bytesToHex(key), time: (Date.now() - started) / 1000 };
				return btoa(JSON.stringify({ challenge, solution }));
			}
		}
	}
	return null;
}

const failed = { status: 0, body: { error: { code: 'GENERIC', details: {} } } };
const sessionLost = (r) => r.status === 401 && r.body?.error?.code === 'SESSION_REQUIRED';

/**
 * @param {{challenge: () => Promise<{status, body}>, open: (payload: string, extra?: object) => Promise<{status, body}>, solver?: Function}} io
 */
export function createSession({ challenge, open, solver = solve }) {
	let current = null;

	/**
	 * @param {{challenge?: {status, body}, extra?: object}} first At page load: the challenge already received with
	 *   POST /start, and extra fields for POST /session (e.g. {calendar: {from, to}}). Renewals fetch a challenge.
	 */
	function renew(first = {}) {
		const attempt = (async () => {
			const c = first.challenge ?? await challenge();
			if (c.status !== 200) {
				return c;
			}
			const payload = await solver(c.body.challenge);
			return payload ? open(payload, first.extra) : failed;
		})();
		current = attempt;
		// A failed attempt is not kept: the next call tries again.
		attempt.then((r) => {
			if (r.status !== 200 && current === attempt) {
				current = null;
			}
		});
		return attempt;
	}

	/** Runs a gated call inside the session; one silent renewal + retry on SESSION_REQUIRED. */
	async function guard(call) {
		const used = current ?? renew();
		const session = await used;
		if (session.status !== 200) {
			return session;
		}
		const r = await call();
		if (!sessionLost(r)) {
			return r;
		}
		const again = await (current === used ? renew() : current ?? renew());
		return again.status === 200 ? call() : again;
	}

	return { start: (first) => current ?? renew(first), guard };
}
